import { useQuery } from "@apollo/client";
import Input from "components/InputGroup/Input";
import Select from "react-select";
import { GET_PARTNER_AND_INSTRUCTOR } from "GraphQl/Queries/User";
import { useMemo, useState } from "react";
import { useRecoilState } from "recoil";
import { currentModuleAccessRecoilVar } from "State/Navigation";
import { gqlUserRecoilVar } from "State/User";
import { GET_COURSES_WITH_FILTER } from "GraphQl/Queries/Courses";
import MultiSelectDropdown from "components/InputGroup/MultiselectDropdown";
import { useEffect } from "react";

const GENDER_STATE_FILTER_PARTNER_ID = 23;
const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];
const STATE_OPTIONS = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
].map((s) => ({ value: s, label: s }));

export default function UsersFilter(props) {
  const {
    updateListingCriteria,
    selectedCourseId,
    candidateCount,
    candidateCountLoading,
  } = props;

  const [user] = useRecoilState(gqlUserRecoilVar);
  const [currentModuleAccess] = useRecoilState(currentModuleAccessRecoilVar);
  const allowedPartnerIds = useMemo(
    () =>
      currentModuleAccess && Object.keys(currentModuleAccess.allowedPartners),
    [currentModuleAccess],
  );

  const [transientListingCriteria, setTransientListingCriteria] = useState({
    partnerId: null,
    email: null,
    name: null,
    mobileNumber: null,
    couponId: null,
    projectId: null,
    courseId: selectedCourseId ? parseInt(selectedCourseId) : null,
    keywords: [],
    gender: null,
    state: null,
  });

  const updateInListingCriteria = (field, bypass) => (value) => {
    console.log({ field, bypass });
    const newListingCriteria = { ...transientListingCriteria, [field]: value };
    setTransientListingCriteria(newListingCriteria);
    if (bypass) updateListingCriteria(newListingCriteria);
  };

  // FETCHING ALL PARTNERS
  const { data: partnersRes } = useQuery(GET_PARTNER_AND_INSTRUCTOR);
  const partners = useMemo(() => {
    const partners = (partnersRes?.courses_partner || []).filter(
      (partner) =>
        user?.is_skillstrainer_admin ||
        (currentModuleAccess?.allowedPartners &&
          currentModuleAccess.allowedPartners[partner.id]),
    );
    return partners;
  }, [user, partnersRes, currentModuleAccess]);
  const selectedPartner =
    partners &&
    partners.find((p) => p.id === transientListingCriteria.partnerId);

  const isGenderStateFilterPartner =
    selectedPartner?.id === GENDER_STATE_FILTER_PARTNER_ID;

  const selectedProject =
    selectedPartner &&
    selectedPartner.projects.find(
      (p) => p.id === transientListingCriteria.projectId,
    );
  const selectedCoupon =
    selectedPartner &&
    selectedPartner.coupons.find(
      (c) => c.id === transientListingCriteria.couponId,
    );
  const selectedGender =
    transientListingCriteria.gender &&
    GENDER_OPTIONS.find((g) => g.value === transientListingCriteria.gender);

  const selectedState =
    transientListingCriteria.state &&
    STATE_OPTIONS.find((s) => s.value === transientListingCriteria.state);

  useEffect(() => {
    if (!selectedPartner) return;

    if (isGenderStateFilterPartner && transientListingCriteria.couponId) {
      updateInListingCriteria("couponId", true)(null);
    }
    if (
      !isGenderStateFilterPartner &&
      (transientListingCriteria.gender || transientListingCriteria.state)
    ) {
      const cleared = {
        ...transientListingCriteria,
        gender: null,
        state: null,
      };
      setTransientListingCriteria(cleared);
      updateListingCriteria(cleared);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenderStateFilterPartner, selectedPartner]);

  // FETCHING ALL COURSES
  // @to-revert
  const courseSearchExp = useMemo(() => {
    if (!user) return { course_id: { _eq: -1 } };
    else if (!user.is_skillstrainer_admin)
      return {
        ...(selectedCourseId ? { id: { _eq: selectedCourseId } } : {}),
        _or: [
          {
            partner_revenue_split_directives: {
              partner_id: { _in: allowedPartnerIds },
            },
          },
          {
            criteria_courses: {
              criteria: {
                coupon: {
                  partner_id: { _in: allowedPartnerIds },
                },
              },
            },
          },
        ],
      };
    else return selectedCourseId ? { id: { _eq: selectedCourseId } } : {};
  }, [user, allowedPartnerIds, selectedCourseId]);

  const coursesHook = useQuery(GET_COURSES_WITH_FILTER, {
    variables: { courseSearchExp },
  });
  const courses = coursesHook.data?.courses_course || [];
  const selectedCourse =
    courses && courses.find((c) => c.id === transientListingCriteria.courseId);

  return (
    <div className="flex-col">
      {isGenderStateFilterPartner && (
        <div className="mb-3 text-sm font-medium text-japanese_indigo">
          {candidateCountLoading
            ? "Loading candidate count..."
            : `Candidate Count: ${candidateCount ?? 0}`}
        </div>
      )}
      <div className="flex-row mb-3 grid grid-cols-2 gap-x-3 gap-y-2">
        <div className="flex flex-col w-full">
          <div className="flex-1">Select Partner</div>
          <div className="flex-1">
            <Select
              className="my-2"
              value={
                selectedPartner
                  ? {
                      label: selectedPartner.name,
                      value: selectedPartner.id,
                    }
                  : { label: "Select a partner" }
              }
              onChange={(e) =>
                updateInListingCriteria("partnerId", true)(e.value)
              }
              options={[{ label: "Select a partner" }].concat(
                partners.map((p) => ({ value: p.id, label: p.name })),
              )}
            />
          </div>
        </div>
        <div className="flex flex-col w-full">
          <div className="flex-1 ">Select Project</div>
          <div className="flex-1">
            <Select
              className="my-2"
              value={
                selectedProject
                  ? {
                      label: selectedProject.name,
                      value: selectedProject.id,
                    }
                  : { label: "Select a project" }
              }
              onChange={(e) =>
                updateInListingCriteria("projectId", true)(e.value)
              }
              options={[{ label: "Select a project" }].concat(
                (selectedPartner &&
                  selectedPartner.projects.map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))) ||
                  [],
              )}
            />
          </div>
        </div>
        {!isGenderStateFilterPartner && (
          <div className="flex flex-col w-full">
            <div className="flex-1 ">Select Coupon</div>
            <div className="flex-1">
              <Select
                className=" my-2"
                value={
                  selectedCoupon
                    ? {
                        label: selectedCoupon.code,
                        value: selectedCoupon.id,
                      }
                    : { label: "Select a coupon" }
                }
                onChange={(e) =>
                  updateInListingCriteria("couponId", true)(e.value)
                }
                options={[{ label: "Select a coupon" }].concat(
                  (selectedPartner &&
                    selectedPartner.coupons.map((c) => ({
                      value: c.id,
                      label: c.code,
                    }))) ||
                    [],
                )}
              />
            </div>
          </div>
        )}
        {isGenderStateFilterPartner && (
          <>
            <div className="flex flex-col w-full">
              <div className="flex-1 ">Select Gender</div>
              <div className="flex-1">
                <Select
                  className=" my-2"
                  value={selectedGender || { label: "Select a gender" }}
                  onChange={(e) =>
                    updateInListingCriteria("gender", true)(e.value || null)
                  }
                  options={[{ label: "Select a gender" }].concat(
                    GENDER_OPTIONS,
                  )}
                  isClearable
                />
              </div>
            </div>
            <div className="flex flex-col w-full">
              <div className="flex-1 ">Select State</div>
              <div className="flex-1">
                <Select
                  className=" my-2"
                  value={selectedState || { label: "Select a state" }}
                  onChange={(e) =>
                    updateInListingCriteria("state", true)(e.value || null)
                  }
                  options={[{ label: "Select a state" }].concat(STATE_OPTIONS)}
                  isClearable
                />
              </div>
            </div>
          </>
        )}
        <div className="flex flex-col w-full">
          <div className="flex-1 ">Select Course</div>
          <div className="flex-1">
            <Select
              className=" my-2"
              value={
                selectedCourse
                  ? {
                      label: selectedCourse.full_name,
                      value: selectedCourse.id,
                    }
                  : { label: "Select a course" }
              }
              onChange={(e) =>
                updateInListingCriteria("courseId", true)(e.value)
              }
              options={[{ label: "Select a course" }].concat(
                courses.map((c) => ({
                  value: c.id,
                  label: c.full_name,
                })) || [],
              )}
            />
          </div>
        </div>
        <div className="flex flex-col w-full">
          <div className="flex-1 ">Keywords</div>
          <div className="flex-1">
            <MultiSelectDropdown
              className=" my-2"
              selectedValue={transientListingCriteria.keywords}
              options={transientListingCriteria.keywords.map((k) => ({
                value: k,
                label: k,
              }))}
              setSelectedValue={(values) =>
                updateInListingCriteria("keywords", true)(values)
              }
              creatable
            />
          </div>
        </div>
      </div>
      <div className="mb-4">
        <p className="mt-2 text-japanese_indigo">User details: </p>
        <div className="grid grid-cols-3 gap-3">
          <div className="flex-1 mb-3">
            <Input
              id="search"
              type="text"
              placeholder="Search by Email"
              aria-label="Search Input"
              value={transientListingCriteria.email}
              onChange={(e) => updateInListingCriteria("email")(e.target.value)}
              onBlur={(e) =>
                updateInListingCriteria("email", true)(e.target.value)
              }
            />
          </div>
          <div className="flex-1 mb-3">
            <Input
              id="name"
              type="text"
              placeholder="Search by Name"
              aria-label="Search Name"
              value={transientListingCriteria.name}
              onChange={(e) => updateInListingCriteria("name")(e.target.value)}
              onBlur={(e) =>
                updateInListingCriteria("name", true)(e.target.value)
              }
            />
          </div>
          <div className="flex-1 mb-3">
            <Input
              id="mobile_number"
              type="text"
              placeholder="Search by Mobile Number"
              aria-label="Search Mobile Number"
              value={transientListingCriteria.mobileNumber}
              onChange={(e) =>
                updateInListingCriteria("mobileNumber")(e.target.value)
              }
              onBlur={(e) =>
                updateInListingCriteria("mobileNumber", true)(e.target.value)
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
